import React from 'react';
import {
  Box, Grid, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, Checkbox, CircularProgress,
  Paper, Autocomplete, Chip, Stack,
} from '@mui/material';
import { getRequest, postRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL, PATCH_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { VisibilityOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function nowDateTimeLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function CompanyPayment(props) {
  const params = new URLSearchParams(window.location.search);
  const isManual = params.get('manual') === '1';
  // Invoice flow may pass year/company; manual flow ignores previous-screen values.
  const initialYearId = isManual ? '' : (params.get('year') || '');
  const initialCompanyId = isManual ? '' : (params.get('company') || '');
  const paymentId = params.get('payment_id');
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [summaryLoading, setSummaryLoading] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [paidInvoiceRows, setPaidInvoiceRows] = React.useState([]);
  const [unpaidInvoiceRows, setUnpaidInvoiceRows] = React.useState([]);
  const [paymentItems, setPaymentItems] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [companies, setCompanies] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState(initialYearId);
  const [selectedCompany, setSelectedCompany] = React.useState(null);
  const [manualCompanyName, setManualCompanyName] = React.useState('');
  const [form, setForm] = React.useState({
    payment_date: '',
    method: 'bank_transfer',
    cheque_status: 'received',
    reference: '',
    notes: '',
    collected_by: '',
    amount: '',
    is_gst_applicable: false,
    gst_percentage: '18',
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [paymentItemErrors, setPaymentItemErrors] = React.useState({});

  const yearId = selectedYear;
  const companyId = selectedCompany?.company || (!isManual ? initialCompanyId : '') || '';
  const companyName = isManual
    ? manualCompanyName
    : (selectedCompany?.company_name || summary?.company_name || '');
  const billingConfigId = isManual ? null : (summary?.billing_config || null);

  const METHOD_CHOICES = ['cash', 'bank_transfer_unaccountable', 'bank_transfer', 'upi', 'cheque', 'gateway'];
  const CHEQUE_STATUS_CHOICES = [
    { value: 'received', label: 'Received' },
    { value: 'deposited', label: 'Deposited' },
    { value: 'transacted', label: 'Transacted' },
  ];

  const extraAmount = React.useMemo(() => {
    return (paymentItems || []).reduce((sum, it) => {
      if (!it.selected) return sum;
      const amt = parseFloat(it.amount_paid || 0) || 0;
      const bal = parseFloat(it.balance || 0) || 0;
      return sum + Math.max(0, amt - bal);
    }, 0);
  }, [paymentItems]);

  const getUsers = () => {
    return getRequest(GET_URL.user.api, { groups: '3,4' }, props).then((response) => {
      if (response && response.status === 200) {
        setUsers(response.data.data || response.data || []);
      }
    });
  };

  const getAcademicYearList = () => {
    return getRequest(GET_URL.academicyear.api, {}, props).then((response) => {
      if (response && response.status === 200) {
        setAcademicYearList(response.data.data || []);
      }
    });
  };

  const getCompanies = (year) => {
    // Manual payments use all companies (not billing-config filtered).
    // year is unused for the company list but kept for call-site compatibility.
    return getRequest(GET_URL.company.api, { limit: 10000, pageno: 1 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          const raw = response.data.data || response.data || [];
          const data = (Array.isArray(raw) ? raw : []).map((c) => ({
            company: c.id,
            company_name: c.name || c.company_name || '',
            id: c.id,
          }));
          setCompanies(data);
          return data;
        }
        setCompanies([]);
        return [];
      })
      .catch(() => {
        setCompanies([]);
        return [];
      });
  };

  const getInvoiceSummary = (yId = yearId, cId = companyId) => {
    if (isManual) return Promise.resolve(null);
    if (!yId || !cId) {
      setSummary(null);
      setPaidInvoiceRows([]);
      setUnpaidInvoiceRows([]);
      setPaymentItems([]);
      return Promise.resolve(null);
    }
    const reqParams = { year: yId, company: cId, invoice_summary: 1 };
    if (paymentId) reqParams.exclude_payment = paymentId;
    setSummaryLoading(true);
    return getRequest(GET_URL.payments.api, reqParams, props)
      .then((response) => {
        const payload = response?.data?.data || response?.data;
        setSummary(payload || null);
        const invs = Array.isArray(payload?.invoices) ? payload.invoices : [];
        const paidInvs = Array.isArray(payload?.paid_invoices) ? payload.paid_invoices : [];
        const unpaidInvs = Array.isArray(payload?.unpaid_invoices) ? payload.unpaid_invoices : [];
        setPaidInvoiceRows(paidInvs);
        setUnpaidInvoiceRows(unpaidInvs);
        const baseItems = unpaidInvs.map((inv) => ({
          selected: false,
          invoice: inv.id,
          invoice_number: inv.invoice_number,
          total_amount: inv.total_amount,
          total_paid: inv.total_paid,
          balance: inv.balance,
          amount_paid: '',
          notes: '',
        }));
        setPaymentItems(baseItems);
        return { invs, paidInvs, unpaidInvs, baseItems };
      })
      .finally(() => setSummaryLoading(false));
  };

  const getPayment = () => {
    if (!paymentId) return Promise.resolve();
    return getRequest(`${GET_URL.payments.api}${paymentId}/`, {}, props).then((response) => {
      const payload = response?.data?.data || response?.data;
      if (!payload) return;
      setForm({
        payment_date: payload.payment_date ? String(payload.payment_date).slice(0, 16) : '',
        method: payload.method ?? 'bank_transfer',
        cheque_status: payload.cheque_status || 'received',
        reference: payload.reference ?? '',
        notes: payload.notes ?? '',
        collected_by: payload.collected_by ?? '',
        amount: payload.amount != null ? String(payload.amount) : '',
      });
      const existingItems = Array.isArray(payload?.items) ? payload.items : [];
      return { existingItems, billing_config: payload.billing_config, amount: payload.amount };
    });
  };

  React.useEffect(() => {
    const boot = async () => {
      setLoading(true);
      try {
        await Promise.all([getUsers(), getAcademicYearList()]);
        if (isManual) {
          // Fresh manual entry — nothing from list filters / URL year-company.
          setSelectedYear('');
          setSelectedCompany(null);
          setManualCompanyName('');
          if (!paymentId) {
            setForm((prev) => ({
              ...prev,
              payment_date: prev.payment_date || nowDateTimeLocalISO(),
            }));
          }
          return;
        }
        if (initialYearId) {
          const companyList = await getCompanies(initialYearId);
          if (initialCompanyId) {
            const match = (companyList || []).find(
              (c) => String(c.company) === String(initialCompanyId)
            );
            if (match) setSelectedCompany(match);
          }
        }
        const results = await Promise.all([
          getInvoiceSummary(initialYearId, initialCompanyId),
          getPayment(),
        ]);
        const summaryRes = results?.[0] || {};
        const paymentRes = results?.[1];
        const existingItems = Array.isArray(paymentRes?.existingItems) ? paymentRes.existingItems : [];
        if (existingItems.length > 0) {
          const invs = Array.isArray(summaryRes.invs) ? summaryRes.invs : [];
          const unpaidInvs = Array.isArray(summaryRes.unpaidInvs) ? summaryRes.unpaidInvs : [];
          const invMeta = new Map(invs.map((inv) => [String(inv.id), inv]));
          const baseByInv = new Map((unpaidInvs || []).map((inv) => [String(inv.id), {
            selected: false,
            invoice: inv.id,
            invoice_number: inv.invoice_number,
            total_amount: inv.total_amount,
            total_paid: inv.total_paid,
            balance: inv.balance,
            amount_paid: '',
            notes: '',
          }]));
          for (const it of existingItems) {
            const invId = it.invoice || it.invoice_id;
            if (!invId) continue;
            const meta = invMeta.get(String(invId)) || {};
            const existing = baseByInv.get(String(invId)) || {};
            baseByInv.set(String(invId), {
              ...existing,
              selected: true,
              invoice: invId,
              invoice_number: existing.invoice_number || meta.invoice_number || it.invoice_number || invId,
              total_amount: existing.total_amount ?? meta.total_amount ?? '',
              total_paid: existing.total_paid ?? meta.total_paid ?? '',
              balance: existing.balance ?? meta.balance ?? '',
              amount_paid: it.amount_paid ?? '',
              notes: it.notes ?? '',
            });
          }
          setPaymentItems(Array.from(baseByInv.values()));
        }
        if (!paymentId) {
          setForm((prev) => ({
            ...prev,
            payment_date: prev.payment_date || nowDateTimeLocalISO(),
          }));
        }
      } finally {
        setLoading(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId, isManual]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    if (!isManual) {
      setSelectedCompany(null);
      getCompanies(year);
    }
  };

  const handleCompanyChange = (_e, value) => {
    setSelectedCompany(value || null);
    if (!isManual && value?.company && selectedYear) {
      getInvoiceSummary(selectedYear, value.company);
    } else if (!isManual && !value) {
      setSummary(null);
      setPaidInvoiceRows([]);
      setUnpaidInvoiceRows([]);
      setPaymentItems([]);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'amount' || name === 'gst_percentage') {
      const numericValue = String(value).replace(/[^0-9.]/g, '');
      const parts = numericValue.split('.');
      next = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : numericValue;
    }
    if (name === 'is_gst_applicable') {
      next = value === true || value === 'true';
    }
    setForm((prev) => {
      const updated = { ...prev, [name]: next };
      if (name === 'method') {
        updated.cheque_status = next === 'cheque' ? (prev.cheque_status || 'received') : 'received';
      }
      return updated;
    });
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const onChangePaymentItem = (idx, field, value) => {
    if (field === 'amount_paid') {
      const numericValue = value.replace(/[^0-9.]/g, '');
      const parts = numericValue.split('.');
      const sanitizedValue = parts.length > 2
        ? `${parts[0]}.${parts.slice(1).join('')}`
        : numericValue;
      setPaymentItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: sanitizedValue, selected: true } : it)));
    } else {
      setPaymentItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value, selected: true } : it)));
    }
    const errorKey = `${idx}_${field}`;
    if (paymentItemErrors[errorKey]) {
      setPaymentItemErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  const onToggleAll = (checked) => {
    setPaymentItems((prev) => (prev || []).map((it) => ({ ...it, selected: !!checked })));
  };

  const onToggleOne = (idx, checked) => {
    setPaymentItems((prev) => prev.map((it, i) => (
      i === idx ? { ...it, selected: !!checked, amount_paid: checked ? it.balance : '' } : it
    )));
  };

  const validateManual = () => {
    const errors = {};
    let isValid = true;
    if (!selectedYear) {
      errors.year = 'Academic Year is required';
      isValid = false;
    }
    if (!manualCompanyName || !manualCompanyName.trim()) {
      errors.company_name = 'Company name is required';
      isValid = false;
    }
    if (!form.payment_date || form.payment_date.trim() === '') {
      errors.payment_date = 'Payment Date is required';
      isValid = false;
    }
    const amount = parseFloat(form.amount || 0);
    if (isNaN(amount) || amount <= 0) {
      errors.amount = 'Amount is required and must be greater than 0';
      isValid = false;
    }
    if (!form.collected_by) {
      errors.collected_by = 'Collected By is required';
      isValid = false;
    }
    if (form.is_gst_applicable) {
      const gstPct = parseFloat(form.gst_percentage);
      if (isNaN(gstPct) || gstPct < 0 || gstPct > 100) {
        errors.gst_percentage = 'GST % must be between 0 and 100';
        isValid = false;
      }
    }
    setFormErrors(errors);
    return isValid;
  };

  const validateInvoicePayment = () => {
    const errors = {};
    const itemErrs = {};
    let isValid = true;

    if (!form.payment_date || form.payment_date.trim() === '') {
      errors.payment_date = 'Payment Date is required';
      isValid = false;
    }
    if (!form.collected_by) {
      errors.collected_by = 'Collected By is required';
      isValid = false;
    }

    let hasSelectedItems = false;
    for (let idx = 0; idx < (paymentItems || []).length; idx++) {
      const item = paymentItems[idx];
      if (!item.selected) continue;
      hasSelectedItems = true;
      const amountPaid = parseFloat(item.amount_paid || 0);
      if (isNaN(amountPaid) || amountPaid <= 0) {
        itemErrs[`${idx}_amount_paid`] = 'Pay Now is required and must be greater than 0';
        isValid = false;
      }
    }

    if (!hasSelectedItems) {
      Swal.fire({
        type: 'warning',
        title: 'No invoices selected',
        text: 'Select at least one invoice and enter Pay Now amount.',
      });
      isValid = false;
    }

    setFormErrors(errors);
    setPaymentItemErrors(itemErrs);
    return isValid;
  };

  const handleManualSubmit = () => {
    if (!validateManual()) return;
    const yearObj = academicYearList.find((y) => String(y.id) === String(selectedYear));
    const payload = {
      company_name: manualCompanyName.trim(),
      year: Number(selectedYear),
      year_name: yearObj?.name || '',
      amount: parseFloat(form.amount),
      payment_date: form.payment_date || undefined,
      method: form.method,
      cheque_status: form.method === 'cheque' ? (form.cheque_status || 'received') : 'received',
      reference: form.reference || '',
      notes: form.notes || '',
      collected_by: form.collected_by ? Number(form.collected_by) : undefined,
      is_gst_applicable: Boolean(form.is_gst_applicable),
      gst_percentage: form.is_gst_applicable ? parseFloat(form.gst_percentage || 18) : null,
    };
    setSubmitting(true);
    postRequest(POST_URL.manual_payments.api, payload, props)
      .then((response) => {
        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: 'Success',
            text: 'Manual payment created',
            showConfirmButton: false,
            timer: 1500,
          });
          navigate('/billing/payment/list');
        }
      })
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  const handleSubmit = () => {
    if (!summary?.billing_config) return;
    if (!validateInvoicePayment()) return;

    const items = [];
    let totalPaid = 0;
    for (const payment of (paymentItems || [])) {
      if (!payment.selected) continue;
      const amt = parseFloat(payment.amount_paid || 0) || 0;
      if (amt <= 0) continue;
      totalPaid += amt;
      items.push({
        invoice: payment.invoice,
        amount_paid: amt,
        notes: payment.notes || '',
      });
    }
    const payload = {
      billing_config: Number(summary.billing_config),
      amount: totalPaid,
      payment_date: form.payment_date || undefined,
      method: form.method,
      cheque_status: form.method === 'cheque' ? (form.cheque_status || 'received') : 'received',
      reference: form.reference || '',
      notes: form.notes || '',
      collected_by: form.collected_by ? Number(form.collected_by) : undefined,
      items,
    };

    setSubmitting(true);
    const req = paymentId
      ? patchRequest(`${PATCH_URL.payments.api}${paymentId}/`, payload, props)
      : postRequest(POST_URL.payments.api, payload, props);

    req.then((response) => {
      if (response && response.status === 200) {
        Swal.fire({
          position: 'top-end',
          type: 'success',
          title: 'Success',
          text: paymentId ? 'Payment updated' : 'Payment created',
          showConfirmButton: false,
          timer: 1500,
        });
        navigate('/billing/payment/list');
      }
    }).catch(() => {}).finally(() => setSubmitting(false));
  };

  const yearLabel = academicYearList.find((y) => String(y.id) === String(selectedYear))?.name;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // ===================== MANUAL PAYMENT (no invoices) =====================
  if (isManual && !paymentId) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', bgcolor: 'background.default' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Manual payment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Enter all fields yourself. This is not linked to invoices.
            </Typography>
            {(companyName || yearLabel) && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {companyName && (
                  <Chip label={companyName} color="primary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
                )}
                {yearLabel && <Chip label={yearLabel} size="small" />}
                <Chip label="Manual" size="small" color="warning" />
              </Stack>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlined />}
            onClick={() => navigate('/billing/payment/list')}
            sx={{ textTransform: 'none', borderRadius: 2, alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            View payments
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Company & year
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.year}>
                <InputLabel>Academic Year</InputLabel>
                <Select value={selectedYear} label="Academic Year" onChange={handleYearChange}>
                  {academicYearList.map((y) => (
                    <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                  ))}
                </Select>
                {formErrors.year && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.year}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company name"
                value={manualCompanyName}
                onChange={(e) => {
                  setManualCompanyName(e.target.value);
                  if (formErrors.company_name) {
                    setFormErrors((prev) => {
                      const next = { ...prev };
                      delete next.company_name;
                      return next;
                    });
                  }
                }}
                fullWidth
                size="small"
                required
                error={!!formErrors.company_name}
                helperText={formErrors.company_name || 'Type the company name (not linked to company master)'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            Payment details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Amount (₹)"
                name="amount"
                value={form.amount}
                onChange={onChange}
                fullWidth
                size="small"
                required
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Payment Date"
                type="datetime-local"
                name="payment_date"
                value={form.payment_date}
                onChange={onChange}
                fullWidth
                size="small"
                required
                error={!!formErrors.payment_date}
                helperText={formErrors.payment_date}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select label="Method" name="method" value={form.method} onChange={onChange}>
                  {METHOD_CHOICES.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {form.method === 'cheque' && (
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cheque Status</InputLabel>
                  <Select
                    label="Cheque Status"
                    name="cheque_status"
                    value={form.cheque_status || 'received'}
                    onChange={onChange}
                  >
                    {CHEQUE_STATUS_CHOICES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Reference"
                name="reference"
                value={form.reference}
                onChange={onChange}
                fullWidth
                size="small"
                error={!!formErrors.reference}
                helperText={formErrors.reference}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" required error={!!formErrors.collected_by}>
                <InputLabel>Collected By</InputLabel>
                <Select
                  label="Collected By"
                  name="collected_by"
                  value={form.collected_by}
                  onChange={onChange}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.username || u.email || u.id}</MenuItem>
                  ))}
                </Select>
                {formErrors.collected_by && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.collected_by}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>GST Applicable</InputLabel>
                <Select
                  label="GST Applicable"
                  name="is_gst_applicable"
                  value={form.is_gst_applicable ? 'true' : 'false'}
                  onChange={(e) => {
                    onChange({
                      target: {
                        name: 'is_gst_applicable',
                        value: e.target.value === 'true',
                      },
                    });
                  }}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.is_gst_applicable && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="GST %"
                  name="gst_percentage"
                  value={form.gst_percentage}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  required
                  error={!!formErrors.gst_percentage}
                  helperText={formErrors.gst_percentage || 'Amount is treated as inclusive of GST'}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                value={form.notes}
                onChange={onChange}
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder="Optional notes for this manual payment"
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate('/billing/payment/list')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleManualSubmit}
              disabled={submitting}
              sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
            >
              {submitting ? 'Saving…' : 'Create manual payment'}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // ===================== INVOICE-BASED PAYMENT =====================
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', bgcolor: 'background.default' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {paymentId ? 'Edit payment' : 'Add payment'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Record invoice-wise collections with method, reference, and notes.
          </Typography>
          {companyName ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip label={companyName} color="primary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
              {(summary?.year_name || yearLabel) && (
                <Chip label={summary?.year_name || yearLabel} size="small" />
              )}
            </Stack>
          ) : null}
        </Box>
        <Button
          variant="contained"
          startIcon={<VisibilityOutlined />}
          onClick={() => navigate('/billing/payment/list')}
          sx={{ textTransform: 'none', borderRadius: 2, alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          View payments
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        {(summary && (summary.company_name || summary.year_name || companyName)) && (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'grey.50' }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Billing summary
            </Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Typography variant="caption" color="textSecondary">
                Company: {companyName || summary.company_name || '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Academic Year: {summary.year_name ?? summary.year ?? '-'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Invoices: {Array.isArray(summary.invoices) ? summary.invoices.length : 0}
              </Typography>
              {extraAmount > 0 && (
                <Typography variant="caption" color="textSecondary">
                  Extra amount: {extraAmount.toFixed(2)}
                </Typography>
              )}
            </Box>
          </Paper>
        )}

        {summaryLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!summaryLoading && yearId && companyId && unpaidInvoiceRows.length > 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Payment Date"
                type="datetime-local"
                name="payment_date"
                value={form.payment_date}
                onChange={onChange}
                fullWidth
                size="small"
                required
                error={!!formErrors.payment_date}
                helperText={formErrors.payment_date}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select label="Method" name="method" value={form.method} onChange={onChange}>
                  {METHOD_CHOICES.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {form.method === 'cheque' && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cheque Status</InputLabel>
                  <Select
                    label="Cheque Status"
                    name="cheque_status"
                    value={form.cheque_status || 'received'}
                    onChange={onChange}
                  >
                    {CHEQUE_STATUS_CHOICES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Reference"
                name="reference"
                value={form.reference}
                onChange={onChange}
                fullWidth
                size="small"
                error={!!formErrors.reference}
                helperText={formErrors.reference}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" required error={!!formErrors.collected_by}>
                <InputLabel>Collected By</InputLabel>
                <Select
                  label="Collected By"
                  name="collected_by"
                  value={form.collected_by}
                  onChange={onChange}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.username || u.email || u.id}</MenuItem>
                  ))}
                </Select>
                {formErrors.collected_by && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.collected_by}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                name="notes"
                value={form.notes}
                onChange={onChange}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        ) : null}

        {!summaryLoading && (!yearId || !companyId) && (
          <Typography variant="body2" color="error">
            Year and company are required. Example: /billing/payment?year=1&company=8
          </Typography>
        )}

        {!summaryLoading && yearId && companyId && unpaidInvoiceRows.length > 0 ? (
          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Unpaid / Partially Paid Invoices
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={(paymentItems || []).length > 0 && (paymentItems || []).every((x) => !!x.selected)}
                      indeterminate={(paymentItems || []).some((x) => !!x.selected) && !(paymentItems || []).every((x) => !!x.selected)}
                      onChange={(e) => onToggleAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Pay Now</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentItems.map((it, idx) => (
                  <TableRow key={`inv-pay-${it.invoice}`}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={!!it.selected}
                        onChange={(e) => onToggleOne(idx, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{it.invoice_number || it.invoice}</TableCell>
                    <TableCell align="right">{it.total_amount ?? '-'}</TableCell>
                    <TableCell align="right">{it.total_paid ?? '-'}</TableCell>
                    <TableCell align="right">{it.balance ?? '-'}</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={it.amount_paid ?? ''}
                        onChange={(e) => onChangePaymentItem(idx, 'amount_paid', e.target.value)}
                        placeholder="0.00"
                        required={!!it.selected}
                        error={!!paymentItemErrors[`${idx}_amount_paid`]}
                        helperText={paymentItemErrors[`${idx}_amount_paid`]}
                        inputProps={{ min: 0.01, step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={it.notes ?? ''}
                        onChange={(e) => onChangePaymentItem(idx, 'notes', e.target.value)}
                        fullWidth
                        error={!!paymentItemErrors[`${idx}_notes`]}
                        helperText={paymentItemErrors[`${idx}_notes`]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (!summaryLoading && yearId && companyId ? (
          <Typography mt={2} fontWeight={600}>No Pending Payments found</Typography>
        ) : null)}

        {paidInvoiceRows.length > 0 && (
          <Box mt={3}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Paid Invoices</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paidInvoiceRows.map((inv) => (
                  <TableRow key={`paid-${inv.id}`}>
                    <TableCell>{inv.invoice_number || inv.id}</TableCell>
                    <TableCell align="right">{inv.total_amount ?? '-'}</TableCell>
                    <TableCell align="right">{inv.total_paid ?? '-'}</TableCell>
                    <TableCell align="right">{inv.balance ?? '-'}</TableCell>
                    <TableCell>{inv.status ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {unpaidInvoiceRows.length > 0 ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || summaryLoading}
                sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
              >
                {paymentId ? 'Update' : 'Create'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/billing/payment/list')}
                sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}
              >
                Cancel
              </Button>
            </Box>
          </>
        ) : null}
      </Paper>
    </Box>
  );
}

export default CompanyPayment;
